
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { debounceTime, filter } from 'rxjs/operators';
import { ComposerApiService } from '@services/composer-api.service';
import { GlobalSearchApiService } from '@services/global-search-api.service';
import { ProductEnrichmentApiService } from '@services/product-enrichment-api.service';
import { FolderApiService } from '@services/folder-api.service';
import { UserProfileApiService } from '@services/user-profile-api.service';
import { GlobalDataService } from '@services/global-data.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import {
  SearchBarComponent,
  CheckboxComponent,
  ObjectCardComponent,
  ButtonComponent,
  DropdownMenuComponent
} from '@mds/ui';
import { CommonDialogWrapperComponent } from '../common-dialog-wrapper/common-dialog-wrapper.component';
import { CommonDialogButton } from '../common-dialog-wrapper/common-dialog-wrapper.model';
import { Product } from './product-search-dialog.model';

@Component({
  selector: 'app-product-search-dialog',
  standalone: true,
  imports: [
    SearchBarComponent,
    FormsModule,
    ReactiveFormsModule,
    CheckboxComponent,
    ObjectCardComponent,
    CommonDialogWrapperComponent,
    ButtonComponent,
    DropdownMenuComponent
],
  templateUrl: './product-search-dialog.component.html',
  styleUrls: ['./product-search-dialog.component.scss']
})
export class ProductSearchDialogComponent implements OnInit {
  dataSetTypes: any[];
  selectedType = new UntypedFormControl();
  multiSelect: boolean;
  selectTypeOption: boolean;
  selectedTiles: any[] = [];
  selectedApp: any;
  maxSelection = 0;
  selectOption: boolean;

  placeholder = 'Search products to enrich';
  searchText = new FormControl('');
  searchResults: any[] = [];
  selectedTilesRid: string[] = [];
  searchResultsCount = 0;

  headerText = 'Enrich Products';
  confirmButton: CommonDialogButton = { text: 'Enrich', type: 'primary' };
  cancelButton: CommonDialogButton = { text: 'Cancel', type: 'secondary' };

  submitButtonText = 'Enrich';
  selectAllText = 'Select all to import';
  deSelectAllText = 'Deselect all to import';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private globalSearchApiService: GlobalSearchApiService,
    private productEnrichmentApiService: ProductEnrichmentApiService,
    private folderApiService: FolderApiService,
    private userProfileApiService: UserProfileApiService,
    private composerApiService: ComposerApiService,
    private globalDataService: GlobalDataService,
    private eventDelegatorService: EventDelegatorService,
    public dialogClose: MatDialogRef<ProductSearchDialogComponent>,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.init();
  }

  init() {
    if (!this.data) return;

    this.setDefaults();
    this.setDataSetTypes();
    this.setSelectOptions();
    this.setPlaceholdersBasedOnAction();

    this.initSearch();
    this.getData();
  }

  private setDefaults() {
    this.placeholder = 'Search data objects';
    this.submitButtonText = 'Select';
    this.selectAllText = 'Select all';
    this.deSelectAllText = 'Deselect all';
    this.selectedTilesRid = [];
    this.maxSelection = this.data.maxSelection || 0;
    this.headerText = this.data.headerText;
  }

  private setDataSetTypes() {
    this.dataSetTypes = this.globalDataService
      .getAllDataTypes()
      .map((item) => ({
        ...item,
        text: item.label
      }));
  }

  private setSelectOptions() {
    const excludeTypes = [
      'product-search',
      'pef-product-search',
      'enrich-search',
      'product-and-product-group-search',
      'performance-modal'
    ];
    this.selectOption = !excludeTypes.includes(this.data.type);
    this.multiSelect = !!this.data.multiple;
    this.selectTypeOption = !!this.data.selectOption;
    this.selectedApp = this.data.selectedApp;

    const selectedType = this.data.selectType || this.dataSetTypes[0];
    this.selectedType.setValue(selectedType);
  }

  private setPlaceholdersBasedOnAction() {
    switch (this.data.action) {
      case 'mysite-product-and-product-group-search':
        this.placeholder = 'Search products and product groups';
        break;
      case 'enrich-product-search':
        this.placeholder = 'Search the web';
        this.submitButtonText = 'Enrich';
        this.selectAllText = 'Select all to import';
        this.deSelectAllText = 'Deselect all to import';
        break;

      case 'compare-products-search':
        this.submitButtonText = 'Compare Product';
        break;
      case 'performance-modal':
        this.placeholder = 'Search for Product Groups';
        break;
    }
  }

  // ----------searchText form control------------//
  initSearch() {
    this.handleSearchInput();
    this.handleTypeChange();
  }

  private handleSearchInput() {
    this.searchText.valueChanges
      .pipe(
        filter((value) => typeof value === 'string' && value.length >= 1),
        debounceTime(500)
      )
      .subscribe({
        next: (value) => this.fetchData(value),
        error: (err) => console.error('Search input error:', err)
      });
  }

  private handleTypeChange() {
    this.selectedType.valueChanges.subscribe({
      next: () => this.fetchData(this.searchText.value),
      error: (err) => console.error('Type change error:', err)
    });
  }

  private fetchData(value: string = '') {
    const type = this.resolveSearchType();

    if (type === 'all') {
      this.folderApiService.mysiteSearch(value, type, 1).subscribe((res) => {
        this.reformat(res);
      });
    } else if (type === 'enrich') {
      this.productEnrichmentApiService
        .getEnrichmentSearchResult(this.data.rid, value)
        .subscribe((res: Product[][]) => {
          const results = Object.values(res)
            .filter(Boolean)
            .map((arr: any[]) => ({
              title: arr[0]?.value,
              image: arr[1]?.value,
              description: arr[2]?.value,
              enrich_company: arr[3]?.value,
              key: arr[4]?.value,
              rid: arr[4]?.value
            }));
          this.reformat(results);
        });
    } else {
      this.globalSearchApiService.globalsearchApi(value, type).subscribe((res) => {
        this.reformat(res);
      });
    }
  }

  private resolveSearchType(): string | string[] {
    const { type, action } = this.data;
    const selected = this.selectedType?.value?.value;

    if (!this.selectTypeOption) {
      if (type === 'product-search') {
        if (
          ['favorite-products-search', 'app-selection-modal'].includes(action)
        ) {
          return ['sku', 'product'];
        }
        if (action === 'performance-modal') {
          return ['product', 'process', 'sku'];
        }
        if (['example-modal', 'compare-products-search'].includes(action)) {
          return selected;
        }
      }

      if (
        type === 'product-and-product-group-search' &&
        action === 'mysite-product-and-product-group-search'
      ) {
        return 'all';
      }

      if (type === 'enrich-search' && action === 'enrich-product-search') {
        return 'enrich';
      }
    }

    return selected;
  }

  reformat(res: any) {
    const searchData: Array<{ label: string; cards: any[] }> = [];

    if (Array.isArray(res)) {
      searchData.push({
        label: 'Results',
        cards: res
      });
    } else {
      const data: Record<string, any> = res?.cards || res || {};
      const entries = Object.entries(data);

      for (const [key, rawValue] of entries) {
        if (key === 'search') continue;

        let label = key.replace(/_/g, ' ');
        if (label === 'recent') {
          label = 'Recently viewed products';
        }

        const cards = Array.isArray(rawValue?.cards)
          ? rawValue.cards
          : Array.isArray(rawValue)
            ? rawValue
            : [];

        searchData.push({ label, cards });
      }
    }

    this.searchResults = searchData;
    this.searchResultsCount = this.searchResults?.[0]?.cards?.length ?? 0;
  }
  // ----------searchText form control end------------//

  getData() {
    if (this.data.defaultLoadProduct) {
      this.reformat(this.data.defaultLoadProduct);
      if (
        (this.data.type === 'product-search' &&
          this.data.action === 'favorite-products-search') ||
        this.data.type === 'pef-product-search'
      ) {
        this.selectAll();
      }
    } else if (
      this.data.action === 'example-modal' ||
      this.data.action === 'performance-modal'
    ) {
      this.composerApiService.getComposercards('products').subscribe((res) => {
        this.reformat(res);
      });
    } else if (this.data.action === 'enrich-product-search') {
      const resultArray = [];
      this.productEnrichmentApiService
        .getEnrichmentSearchResult(this.data.rid, '')
        .subscribe((res) => {
          for (const m in res) {
            if (res[m]) {
              const obj = {
                title: res[m][0].value,
                image: res[m][1].value,
                description: res[m][2].value,
                enrich_company: res[m][3].value,
                key: res[m][4].value,
                rid: res[m][4].value
              };
              resultArray.push(obj);
            }
          }
          const rest = resultArray;
          this.reformat(rest);
        });
    } else {
      this.globalSearchApiService.getProductAppSearch().subscribe((res) => {
        this.reformat(res);
      });
    }
  }

  updateTilesSelection(data, event?: any) {
    if (event.code && !['Enter', 'Space'].includes(event.code)) return;
    if (!this.multiSelect || data.is_favorite === false) {
      return;
    }
    if (this.selectedTilesRid.includes(data.rid)) {
      this.selectedTilesRid = this.selectedTilesRid.filter(
        (e) => e !== data.rid
      );
    } else {
      if (
        this.maxSelection &&
        this.selectedTilesRid.length >= this.maxSelection
      )
        return;
      if (
        window.location.pathname.split('/').pop() === data.rid.replace('#', '')
      )
        return;

      this.selectedTiles.push(data);
      this.selectedTilesRid.push(data.rid);
    }
  }

  clearSelections() {
    this.selectedTilesRid = [];
  }

  selectAll() {
    if (!this.multiSelect) {
      return;
    }
    this.searchResults.forEach((data) => {
      if (data.cards) {
        data.cards.forEach((e) => {
          const { rid } = e;
          if (!this.selectedTilesRid.includes(rid)) {
            this.selectedTilesRid.push(rid);
          }
        });
      }
    });
  }

  openProduct(product: { rid: string }): void {
    const rid = product.rid.replace('#', '');

    // Handle 'performance-modal' early and exit
    if (this.data.action === 'performance-modal') {
      this.eventDelegatorService.emitter('angularjs-event', {
        name: 'performance-model-reload',
        rid
      });
      this.dialogClose.close(product);
      return;
    }

    let url = '';
    let queryParams: any = {};

    if (!this.selectedApp) {
      url = `/mattermaps/overview/${rid}`;
    } else {
      const { view, layer, module } = this.selectedApp;

      if (!layer && !view && !module) {
        url = `/mattermaps/overview/${rid}`;
      } else if (['sankey', 'graph'].includes(view) || layer === 'dpm') {
        url = `/mattermaps/${layer}/${rid}`;
      } else if (module === 'overview') {
        url = `/mattermaps/${module}/${rid}`;
      } else if (view && view !== 'explorer') {
        url = `/mattermaps/${layer}/${rid}`;
        queryParams = { view };
      } else {
        // Handle known modules
        const moduleRoutes: Record<string, string> = {
          'pef-analysis': `/pef-analysis/${rid}`,
          'ihm-eu-dashboard': `/ihm-eu-dashboard/${rid}`,
          'epd-dashboard': `/epd-dashboard/${rid}`,
          'ecodesign-dashboard': `/ecodesign/${rid}`,
          insights: `/insights/${rid}`,
          explorer: `/explorer/${rid}`,
          'cbam-dashboard': `/cbam-dashboard/${rid}`,
          'scip-notification': `/scip/${rid}`
        };

        url = moduleRoutes[module] ?? `/mattermaps/${layer}/${rid}`;
      }
    }

    this.router.navigate([url], { queryParams });
    this.dialogClose.close(product);
  }

  closeSelectionDialog() {
    this.dialogClose.close({
      rids: this.selectedTilesRid,
      tiles: this.selectedTiles
    });
  }

  mysiteFolderProductAndProductGroup() {
    const listOfMysiteFolder = this.selectedTilesRid;
    this.dialogClose.close(listOfMysiteFolder);
  }

  enrichProductSelect() {
    const listOfEnrichSelectedProduct = this.selectedTilesRid;
    this.dialogClose.close(listOfEnrichSelectedProduct);
  }

  compareProduct() {
    const listOfAddedProducts = this.selectedTilesRid;
    this.dialogClose.close(listOfAddedProducts);
  }

  selectProducts() {
    const { type, action } = this.data;

    if (type === 'product-search') {
      if (action === 'favorite-products-search') {
        this.rearrangeFavoriteProduct();
      } else if (action === 'compare-products-search') {
        this.compareProduct();
      }
    } else if (
      type === 'product-and-product-group-search' &&
      action === 'mysite-product-and-product-group-search'
    ) {
      this.mysiteFolderProductAndProductGroup();
    } else if (type === 'pef-product-search') {
      this.closeSelectionDialog();
    } else if (type === 'enrich-search' && action === 'enrich-product-search') {
      this.enrichProductSelect();
    }
  }

  rearrangeFavoriteProduct() {
    const listOfInitLoadedProduct = this.data.defaultLoadProduct.map(
      (e) => e.rid
    );
    const addedProduct = this.selectedTilesRid.filter(
      (e) => !listOfInitLoadedProduct.includes(e)
    );
    let deletedProduct = this.data.defaultLoadProduct.filter(
      (e) => !this.selectedTilesRid.includes(e.rid)
    );
    deletedProduct = deletedProduct.map((e) => e.rid);

    let count = 0;
    const closeDialog = () => {
      count--;
      if (count === 0) {
        this.closeModal(true);
      }
    };

    if (addedProduct.length > 0 || deletedProduct.length > 0) {
      if (addedProduct.length > 0) {
        count++;
        this.userProfileApiService.addFavoriteProduct(addedProduct).subscribe(
          (res) => {
            closeDialog();
          },
          (err) => {
            closeDialog();
          }
        );
      }

      if (deletedProduct.length > 0) {
        count++;
        this.userProfileApiService
          .removeFavoriteProduct(deletedProduct, 'products')
          .subscribe(
            (res) => {
              closeDialog();
            },
            (err) => {
              closeDialog();
            }
          );
      }
    } else {
      this.dialogClose.close('products');
    }
  }

  closeModal(flag?: boolean) {
    if (flag) {
      this.dialogClose.close('products');
    } else {
      this.dialogClose.close();
    }
  }

  onDialogClose() {
    this.closeModal();
  }
}
